import { ChangeDetectorRef, Component, Input, OnInit, Optional } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { NgbActiveModal, NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ReviewService } from '../../../../services/review.service';
import { Review } from '../../../models/review.model';
import { AlertNotificationsService } from '../../../../services/alert-notifications.service';
import { mustBeChecked, phoneValidator } from '../validators';
import { isPhoneBelongsToClinicError } from '../../../utils/clinic-phone-errors.util';
import { OffcanvasHistoryService } from '../../../../services/offcanvas-history.service';
import { GeoNamesService } from '../../request/service/geonames.service';
import { GeonameLocation } from '../../../models/geoname';

@Component({
  selector: 'app-write-review',
  templateUrl: './write-review.component.html',
  styleUrls: ['./write-review.component.scss'],
  standalone: false
})
export class WriteReviewComponent implements OnInit {
  @Input() clinicName?: string;
  @Input() partnerId?: number;
  @Input() requiredComment = true;
  @Input() isVerified = true;
  @Input() isOffcanvas = false;
  @Input() leadAssignmentId?: number;

  isSubmitting = false;
  reviewForm = new FormGroup({});
  formModel: any = {};
  reviewFields: FormlyFieldConfig[] = [];
  questions: { id: string; text: string }[] = [];

  emojis = ['😠', '😕', '🙂', '😄'];
  emojiIcons = ['sentiment_stressed', 'mood_bad', 'sentiment_calm', 'sentiment_very_satisfied'];
  emojiLabels: string[] = [];

  constructor(
    @Optional() private activeModal: NgbActiveModal,
    private translateService: TranslateService,
    private reviewService: ReviewService,
    public offcanvasService: NgbOffcanvas,
    private offCanvasHistory: OffcanvasHistoryService,
    private notificationsService: AlertNotificationsService,
    private detectorRef: ChangeDetectorRef,
    private geoNames: GeoNamesService
  ) {
    this.questions = [
      {
        id: 'OVERALL_RATING',
        text: this.translateService.instant('Overall rating of the clinic'),
      },
      {
        id: 'PROFESSIONALISM',
        text: this.translateService.instant('Professionalism of the dentist'),
      },
      {
        id: 'CLARITY_EXPLANATIONS',
        text: this.translateService.instant(
          'Clarity and availability in explanations'
        ),
      },
      {
        id: 'RESPECT_TIMES',
        text: this.translateService.instant('Respect for scheduled times'),
      },
      {
        id: 'HYGIENIC_CONDITIONS',
        text: this.translateService.instant(
          'Hygienic conditions of the dental office'
        ),
      },
      {
        id: 'COST_TRANSPARENCY',
        text: this.translateService.instant(
          'Clarity and transparency on the cost of treatment'
        ),
      },
      {
        id: 'TREATMENT_SATISFACTION',
        text: this.translateService.instant(
          'Satisfaction with the treatment received'
        ),
      },
      {
        id: 'EASE_OF_REACH',
        text: this.translateService.instant('Ease of reaching the office'),
      },
      {
        id: 'APPOINTMENT_MANAGEMENT',
        text: this.translateService.instant(
          'Experience in managing the appointment'
        ),
      },
      {
        id: 'WOULD_RETURN',
        text: this.translateService.instant(
          'Would you return to this dental office?'
        ),
      },
    ];

    this.emojiLabels = [
      this.translateService.instant('No never'),
      this.translateService.instant('Maybe not'),
      this.translateService.instant('Probably yes'),
      this.translateService.instant('Definitely yes'),
    ];
  }

  ngOnInit() {
    this.initializeForm();
    if (typeof this.formModel.country === 'string') {
      this.geoNames.getCountries().subscribe((countries: GeonameLocation[]) => {
        this.reviewForm.patchValue({
          country: countries.find(
            c => c.countryCode === this.formModel.country
          )
        });
      });
    }
  }

  getModalTitle(): string {
    return this.clinicName
      ? `${this.translateService.instant('Write a Review for')} ${this.clinicName
      }`
      : this.translateService.instant('Write a Review');
  }

  initializeForm() {
    const rowClass = 'row  px-3';

    this.reviewFields = [
      {
        fieldGroupClassName: 'row px-3',
        fieldGroup: [
          {
            className: 'col-12 col-md-3',
            key: 'name',
            type: 'input',
            props: {
              disabled: this.isVerified,
              label: this.translateService.instant('Name'),
              required: true,
              placeholder: this.translateService.instant('Enter your name'),
              minLength: 2,
            },
          },
          {
            className: 'col-12 col-md-4',
            key: 'country',
            type: 'country-autocomplete',
            props: {
              disabled: this.isVerified,
              label: this.translateService.instant('Country *'),
              required: true,
              placeholder: this.translateService.instant('Select your country'),
            },
          },
          {
            className: 'col-12 col-md-5',
            key: 'phoneNumber',
            wrappers: ['form-field'],
            type: 'phone',
            props: {
              appendTo: 'body',
              disabled: this.isVerified,
              showLabel: false,
              label: this.translateService.instant('Phone number'),
              required: true,
              linkCountryField: 'country',
            },
            validators: {
              phone: phoneValidator,
            },
          },
        ],
      },
      this.isVerified
        ? {
          type: 'template',
          template: `<hr class="separator mb-3 mt-3" >`,
        }
        : {},

      ...(this.isVerified
        ? [
          {
            fieldGroupClassName: rowClass,
            fieldGroup: [
              {
                className: 'col-12',
                key: 'overallRating',
                type: 'rating-buttons',
                props: {
                  label: this.translateService.instant(
                    'Overall rating of the chosen dentist'
                  ),
                  required: true,
                  description: this.translateService.instant(
                    'Select a number from 1 to 10 (1 = very dissatisfied, 10 = completely satisfied)'
                  ),
                },
              },
            ],
          },
        ]
        : []),
      this.isVerified
        ? {
          type: 'template',
          template: `<hr class="separator mb-3 mt-3" >`,
        }
        : {},

      ...(this.isVerified
        ? this.questions.map((question, index) => ({
          fieldGroupClassName: rowClass,
          fieldGroup: [
            {
              className: 'col-12',
              key: `questionRatings.${question.id}`,
              type: 'emoji-rating',
              props: {
                label: `${this.translateService.instant(question.text)}`,
                required: true,
                emojis: this.emojis,
                emojiIcons: this.emojiIcons,
                showLabels: false,
                // showLabels: index === 9,
                emojiLabels: this.emojiLabels,
              },
            },
          ],
        }))
        : []),
      {
        type: 'template',
        template: `<hr class="separator my-2" >`,
      },

      {
        fieldGroupClassName: rowClass,
        fieldGroup: [
          {
            className: 'col-12 mt-2',
            key: 'comment',
            type: 'textarea',
            props: {
              label: `✍️ ${this.translateService.instant('Leave a comment')}`,
              placeholder: this.translateService.instant(
                'Write here a suggestion, an experience or an opinion that you want to share...'
              ),
              rows: 4,
              minLength: 30,
              maxLength: 500,
              required: true,
              keyup: (field, event) => {
                field?.formControl?.setValue(event.target.value);
              },
            },
            hooks: {
              onInit: (field) => {
                field.formControl?.valueChanges.subscribe(() => {
                  this.detectorRef.markForCheck();
                  this.detectorRef.detectChanges();
                });
              },
            },
            expressionProperties: {
              'props.description': (_model, _formState, field) => {
                const currentLength = field?.formControl?.value?.length || 0;
                return (
                  `${currentLength} / ${field?.props?.maxLength} ` +
                  this.translateService.instant('characters entered')
                );
              },
            },
          },
        ],
      },
      {
        fieldGroupClassName: rowClass,
        fieldGroup: [
          {
            className: 'col-12 mt-2',
            key: 'privacy',
            type: 'checkbox',
            props: {
              label: this.translateService.instant(
                'I agree to the Privacy Policy and consent to the processing of my personal data for the purpose of submitting this review.'
              ),
              required: true,
            },
            defaultValue: false,
            validators: {
              mustBeChecked: mustBeChecked,
            },
          },
        ],
      },
    ];
  }

  canSubmit() {
    return !this.isSubmitting;
  }

  async onSubmit() {
    this.reviewForm.markAllAsTouched();

    if (this.reviewForm.invalid) {
      return;
    }

    if (this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;

    try {
      const formValue = this.formModel;

      const rawRatings = formValue.questionRatings || {};
      const simplifiedRatings: { [key: string]: number } = {};

      Object.entries(rawRatings).forEach(([key, value]: [string, any]) => {
        if (value && typeof value.value === 'number') {
          simplifiedRatings[key] = value.value;
        } else if (typeof value === 'number') {
          simplifiedRatings[key] = value;
        }
      });

      const review: any = {
        partnerId: this.partnerId!,
        isVerified: this.isVerified,
        userFlag: formValue.country.countryCode,
        userName: formValue.name,
        nationality: formValue.country?.name,
        description: formValue.comment,
        questionAverages: simplifiedRatings,
        rates: formValue.overallRating,
        phoneNumber: formValue.phoneNumber,
        leadAssignmentId: this.leadAssignmentId,
      };

      const createdReview = await this.reviewService.createReview(review);
      this.notificationsService.success(
        this.translateService.instant('Thank you! Review submitted.')
      );
      if (!this.isOffcanvas) {
        this.activeModal.close({
          success: true,
          data: createdReview,
        });
      } else {
        this.offCanvasHistory.dismiss({
          success: true,
          data: createdReview,
        });
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      if (isPhoneBelongsToClinicError(error)) {
        // Numero di una clinica: errore tradotto sul campo telefono (l'interceptor non mostra toast;
        // l'errore si azzera quando l'utente modifica il numero).
        const phoneControl = this.reviewForm.get('phoneNumber');
        phoneControl?.setErrors({ ...(phoneControl.errors || {}), phoneBelongsToClinic: true });
        phoneControl?.markAsTouched();
        this.notificationsService.error(
          this.translateService.instant('errors.phoneBelongsToClinic')
        );
      } else {
        this.notificationsService.error(
          this.translateService.instant('Failed to submit review')
        );
      }
    } finally {
      this.isSubmitting = false;
    }
  }

  closeOffcanvas() {
    this.offCanvasHistory.dismiss();
  }

  ngOnDestroy() {
    setTimeout(() => {
      document.body.style.overflow = 'auto';
    }, 300);

  }
}